import type { ReactNode } from "react";
import { Focusable } from "react-aria-components";
import { Toggle } from "@/components/base/toggle/toggle";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

type QuarantineToggleRowProps = {
  label: string;
  description: string;
  isSelected: boolean;
  isDisabled?: boolean;
  /** Why the toggle is unavailable, shown on it rather than hiding the row. */
  disabledReason?: { title: string; description?: string };
  /** Right-aligned action shown next to the toggle, e.g. an "Authorise TikTok ads" CTA. */
  action?: ReactNode;
  onChange: (value: boolean) => void;
};

/**
 * One quarantine toggle, shared by the merchant screen and the workspace
 * defaults template.
 *
 * The whole row (not just the switch) is clickable. The switch itself stops
 * propagation on click so its own `onChange` isn't double-triggered by the
 * row's click handler.
 */
export const QuarantineToggleRow = ({
  label,
  description,
  isSelected,
  isDisabled,
  disabledReason,
  action,
  onChange,
}: QuarantineToggleRowProps) => {
  const toggle = (
    <Toggle
      size="md"
      color="success"
      aria-label={label}
      isSelected={isSelected}
      isDisabled={isDisabled}
      onChange={onChange}
    />
  );

  // Both the toggle and the label live inside one trigger, so hovering either shows the same tooltip.
  const toggleAndText = (
    <div className="flex items-center gap-4 flex-1 min-w-0">
      <div className="shrink-0">{toggle}</div>
      <div className="flex-1 min-w-0">
        <p className="text-base font-medium text-primary">{label}</p>
        <p className="text-sm text-quaternary mt-0.5">{description}</p>
      </div>
    </div>
  );

  return (
    <div
      onClick={() => !isDisabled && onChange(!isSelected)}
      className={cx(
        "flex items-center gap-4 px-6 py-5 border-t border-border-primary first:border-t-0",
        isDisabled ? "cursor-not-allowed" : "cursor-pointer",
      )}
    >
      <div className="flex items-center flex-1 min-w-0" onClick={(event) => event.stopPropagation()}>
        {disabledReason ? (
          // Focusable claims the trigger ref — without it the tooltip renders pinned to the viewport corner.
          <Tooltip {...disabledReason}>
            <Focusable isDisabled>{toggleAndText}</Focusable>
          </Tooltip>
        ) : (
          toggleAndText
        )}
      </div>
      {action && (
        <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
          {action}
        </div>
      )}
    </div>
  );
};
